import { useCallback, useEffect, useState } from 'react';
import api, { ApiError } from '../lib/apiClient';

export interface LaravelPage<T> {
  data: T[];
  current_page: number;
  last_page: number;
  total: number;
  per_page: number;
}

export interface ListFilters {
  search?: string;
  [key: string]: unknown;
}

interface UsePaginatedListOptions {
  /** Extra value (e.g. a parent refresh signal) that triggers a reload
   * without affecting params or page. */
  reloadKey?: unknown;
}

// Shared list-page data loading: fetches `endpoint` with `{ ...filters, page }`,
// debounced 300ms when `filters.search` is set (so typing doesn't fire a
// request per keystroke) and immediately otherwise, and resets to page 1
// whenever the caller passes a new `filters` object (so every filter change
// doesn't need its own setPage(1) in the onChange handler — pass filters via
// useMemo keyed on the actual filter values so identity only changes when a
// value does).
export default function usePaginatedList<T>(endpoint: string, filters: ListFilters = {}, options: UsePaginatedListOptions = {}) {
  const { reloadKey } = options;
  const [page, setPage] = useState(1);
  const [prevFilters, setPrevFilters] = useState(filters);
  const [data, setData] = useState<LaravelPage<T> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  if (filters !== prevFilters) {
    setPrevFilters(filters);
    if (page !== 1) setPage(1);
  }

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api
      .get<LaravelPage<T>>(endpoint, { params: { ...filters, page } })
      .then((res) => setData(res.data))
      .catch((err: ApiError) => setError(err.message || 'Failed to load.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, filters, page]);

  useEffect(() => {
    const t = setTimeout(load, filters.search ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, reloadKey]);

  return { data, loading, error, page, setPage, reload: load };
}
